import React, { useCallback, useEffect, useMemo, useState } from 'react';
import StarNum from '@op/components/StarBubble/StarNum';
import StarBubble from '@op/components/StarBubble';
import BubbleCommon, {
  gameStoreReviewsTips,
} from '@op/components/BubbleCommon';
import { NO_DATE } from '@op/constants/index';
import { Progress, Rate } from 'tea-component';
import { getPercentageValue, isOneDay } from '@op/utils';
import { useIntl } from 'umi';
import moment from 'moment';
import CardCommon from '@op/components/CardCommon';
import './index.scss';
import SvgIcon from '@home/components/SvgIcon';
import { percentageFormat, reviewsFormat, scoreFormat } from '@op/utils/value';
import { useGetRankList } from '../../hooks/rank';
import { RankKeyMeticsItem } from '../../component/TrendCardTips';
import DetailWrapper from '@op/pages/Detail/component/DetailWrapper';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import { getFilterDateQOQText } from '@op/utils/time';

interface IReviewRating {
  platform: string;
  type: string;
  height?: number;
  ratingData: any;
  filter: any;
  games: Array<any>;
  gameData: any;
  showRank: boolean;
  isDetail?: boolean;
  updateDetailFilter?: (filter: any) => void;
  contentCard?: boolean;
  detailPage?: boolean;
}

export default function ReviewRating(props: IReviewRating) {
  const {
    platform,
    type,
    height = 215,
    ratingData,
    filter,
    games,
    gameData,
    showRank,
    isDetail = false,
    updateDetailFilter,
    contentCard = true,
    detailPage = false,
  } = props;

  const { formatMessage } = useIntl();
  const { start_time, end_time } = filter || {};

  const yesterday = moment().subtract(1, 'day');
  const today = moment();

  const isStartWithinPeriod = moment(start_time).isBetween(
    yesterday,
    today,
    'day',
    '[]',
  );
  const isEndWithinPeriod = moment(end_time).isBetween(
    yesterday,
    today,
    'day',
    '[]',
  );

  const isFlag = isStartWithinPeriod && isEndWithinPeriod;

  const params = useMemo(() => {
    return {
      ...filter,
      games: games,
      tab_type: platform,
      by_type: type,
      stats_types: ['game_store'],
    };
  }, [filter, platform, type]);

  const { loading, getRankList } = useGetRankList({
    filter: params,
    gameData,
  });

  const getRateScore = (score: number) => {
    if (platform === 'taptap' && type === 'lifetime') {
      return score / 2;
    }
    return score;
  };
  const [scoreDate, setScoreDate] = useState('');

  function getShowIcon(newValue: number, oldValue: number) {
    const newVal = newValue?.toFixed(4) || 0;
    const oldVal = oldValue?.toFixed(4) || 0;
    if (newVal < oldVal) {
      return 'arrow-other-down';
    }

    if (newVal === oldVal) {
      return 'equal';
    }

    return 'arrow-other-up';
  }

  const { mentions, detail, score, latestTime, scoreText, scorePercent, icon, oldTime, newTime, oldValue, newValue } =
    useMemo(() => {
      const {
        mentions,
        detail,
        score,
        latest_time,
        rationPercent,
        oldTime,
        newTime,
        oldValue,
        newValue,
      } = ratingData || {};
      return {
        mentions: mentions || 0,
        detail: detail || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        score: score || 0,
        latestTime: latest_time || '',
        scoreText: score?.toFixed(1) || 0,
        scorePercent: percentageFormat(rationPercent),
        icon: getShowIcon(newValue, oldValue),
        oldTime,
        newTime,
        oldValue,
        newValue
      };
    }, [ratingData]);

  useEffect(() => {
    let time = '';
    if (type === 'lifetime') {
      time = latestTime;
      if (platform === 'taptap' && !time) {
        if (end_time) {
          time = `Up to ${moment(end_time).format('YYYY-MM-DD')}`;
        }
      }
    } else {
      if (end_time) {
        if (platform === 'app store' && isFlag) {
          time = moment().subtract(2, 'days').format('YYYY-MM-DD');
        } else {
          if (moment(start_time).isSame(end_time, 'd')) {
            time = moment(start_time).format('YYYY-MM-DD');
          } else {
            time = `${moment(start_time).format('YYYY-MM-DD')} to ${moment(
              end_time,
            ).format('YYYY-MM-DD')}`;
          }
        }
      }
    }
    setScoreDate(time);
  }, [end_time, isFlag, latestTime, platform, start_time, type]);
  const { text: beforeText } = getFilterDateQOQText(filter);

  const timeShow = useCallback(
    (timeRange: [string, string]) => {
      const [start_time, end_time] = timeRange;

      if (filter.date_type === 'hourly') {
        return [
          `${moment(start_time).format('YYYY-MM-DD HH:mm')}~${moment(
            end_time,
          ).format('YYYY-MM-DD HH:mm')}`,
        ];
      }
      if (isOneDay(filter)) {
        return [moment(start_time).format('YYYY-MM-DD')];
      }
      return [
        `${moment(start_time).format('YYYY-MM-DD')}~${moment(end_time).format(
          'YYYY-MM-DD',
        )}`,
      ];
    },
    [filter],
  );


  return (
    <CardCommon
      contentCard={contentCard}
      className="dbo-review-rating"
      empty={score === 0}
      hasEmptyTitle={false}
      hasEmptyImg={false}
      emptyDescText={NO_DATE.SELECTION}
      emptyHeight={height}
      title={
        <span>
          {formatMessage({
            id: type === 'lifetime' ? 'Lifetime Rating' : 'Added Reviews',
          })}
          <BubbleCommon
            tipsMap={gameStoreReviewsTips}
            placement="right"
            childStyle={{
              fontWeight: 400,
              fontSize: '12px',
              color: '#9b9fb0',
            }}
          >
            {scoreDate && `(${scoreDate})`}
          </BubbleCommon>
        </span>
      }
      // dropdownsFilterBeforeElement={

      // }
      alwaysShowOperations={true}
    >
      <div className="dbo-compare-operation">
        <RankKeyMeticsItem
          getRankList={getRankList}
          valueKey={'score'}
          valueFormat={scoreFormat}
          noShow={showRank}
          title={type === 'lifetime' ? 'Lifetime Rating' : 'Added Reviews'}
          logId="KMGS_CompetitorRanking"
        ></RankKeyMeticsItem>
      </div>
      <div className="dbo-scorebox-bd">
        <div className="dbo-scorebox-content">
          <div className="dbo-scorebox-score">
            <div className="dbo-scorebox-num">
              {scoreText}
              <RingRatioBubble
                otherInfo={
                  <div className="item-title">
                    {`${formatMessage({
                      id: type === 'lifetime' ? 'Lifetime Rating' : 'Added Reviews',
                    })} ${formatMessage({ id: beforeText })}`}
                  </div>
                }
                newTime={timeShow(newTime)}
                oldTime={timeShow(oldTime)}
                oldValue={oldValue}
                newValue={newValue}
                placement="top"
                formateValue={(val) => reviewsFormat(val, 3)}
              >
                <span className="dbo-scorebox-percent">
                  {scorePercent}
                  <SvgIcon type={icon} />
                </span>
              </RingRatioBubble>

            </div>
          </div>
          <div className="dbo-scorebox-reviewbox">
            <Rate
              readonly
              theme="primary"
              allowHalf
              value={getRateScore(score)}
            />
            <div className="dbo-scorebox-text">
              {reviewsFormat(mentions)}{' '}
              {formatMessage({
                id: type === 'lifetime' ? 'Rating-1' : 'Reviews-4',
              })}
            </div>
          </div>
        </div>
        <DetailWrapper
          eventProps={{
            key: 'KeyMetricsMobileReviewRating',
            defaultFilter: {
              ...filter,
              channel_type: 'comments',
              channel: [platform],
            },
            defaultProps: {
              ...props,
              contentCard: false,
              showRank: false
            },
            paramsKey: 'reviews_rating',
          }}
          isDetail={isDetail}
          updateDetailFilter={updateDetailFilter}
        >
          {({ eventsDict }) => (
            <>
              <StarBubble
                isLifetime={type === 'lifetime'}
                detail={detail}
                mentions={mentions}
                detailPage={detailPage}
              >
                <div className="dbo-scorebox-subcontent">
                  <StarNum />
                  <div className="dbo-scorebox-progressbox">
                    {[5, 4, 3, 2, 1].map((star) => (
                      <div key={star} onClick={() => eventsDict?.(String(star))} className='tea-progress-wrapper'>
                        <Progress
                          key={star}
                          className={`dbo-scorebox-progress star${star}`}
                          percent={getPercentageValue(detail[star], mentions) * 100}
                          strokeWidth={8}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </StarBubble>
            </>
          )}
        </DetailWrapper>
      </div>
    </CardCommon>
  );
}
